import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CoachPortfolioIntelligence } from "@/lib/behavior/viewModels/buildCoachPortfolioIntelligence";

interface CoachPortfolioIntelligenceCardProps {
  intelligence: CoachPortfolioIntelligence;
  onOpenLeaderTieBack?: (reference: {
    label: string;
    route: string;
  }) => void;
  compact?: boolean;
}

export function CoachPortfolioIntelligenceCard({
  intelligence,
  onOpenLeaderTieBack,
  compact = false,
}: CoachPortfolioIntelligenceCardProps) {
  return (
    <Card className="border-border/60 bg-background/90 shadow-sm">
      <CardHeader className={compact ? "pb-3" : undefined}>
        <CardTitle className={compact ? "text-base" : "text-lg"}>{intelligence.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {intelligence.themes.length === 0 ? (
          <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
            <p className="text-sm text-foreground">{intelligence.emptyStateReason}</p>
          </div>
        ) : (
          intelligence.themes.map((theme) => (
            <div key={theme.id} className="rounded-2xl border border-border/60 bg-muted/20 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">{theme.title}</p>
                  <p className="text-sm text-muted-foreground">{theme.whyNow}</p>
                </div>
                <Badge variant="outline">{theme.priorityLabel}</Badge>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Leader tie-back</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onOpenLeaderTieBack?.({
                    label: theme.leaderTieBack.leaderName,
                    route: theme.leaderTieBack.route,
                  })}
                >
                  {theme.leaderTieBack.leaderName}
                </Button>
              </div>
              {theme.supportingSignals.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Signals</p>
                  {theme.supportingSignals.map((signal) => (
                    <p key={signal} className="text-sm text-muted-foreground">{signal}</p>
                  ))}
                </div>
              ) : null}
            </div>
          ))
        )}

        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Confidence</p>
          <p className="text-sm text-foreground">
            <span className="font-medium capitalize">{intelligence.confidenceLabel}</span>
            {" "}
            ·
            {" "}
            {intelligence.confidenceNote}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
